import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Plus,
  Search,
  ChevronLeft,
  Clock,
  Phone,
  Mail,
  User,
  Calendar as Cal,
  MessageCircle,
} from "lucide-react";
import { DataTable, PageTitle } from "@/components/common/Crud";
import { Combobox } from "@/components/common/Combobox";
import { DatePicker } from "@/components/common/DatePicker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const STATUSES = [
  {
    value: "open_interested",
    label: "Open/Interested",
    color: "bg-blue-100 text-blue-700",
  },
  {
    value: "yet_to_visit",
    label: "Yet to Visit",
    color: "bg-amber-100 text-amber-700",
  },
  {
    value: "visited_interested",
    label: "Visited/Interested",
    color: "bg-emerald-100 text-emerald-700",
  },
  {
    value: "visited_not_interested",
    label: "Visited/Not Interested",
    color: "bg-orange-100 text-orange-700",
  },
  { value: "close", label: "Close", color: "bg-slate-100 text-slate-700" },
  {
    value: "admitted",
    label: "Admitted",
    color: "bg-violet-100 text-violet-700",
  },
];
const statusOf = (v) => STATUSES.find((s) => s.value === v) || STATUSES[0];

export default function Enquiries() {
  const { branchId } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [staff, setStaff] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sources, setSources] = useState([]);
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    const { data } = await api.get("/student-enquiries", {
      params: { branch_id: branchId, limit: 500 },
    });
    let items = data.items || [];
    if (filterFrom) items = items.filter((r) => (r.date || "") >= filterFrom);
    if (filterTo) items = items.filter((r) => (r.date || "") <= filterTo);
    if (filterStatus) items = items.filter((r) => r.status === filterStatus);
    if (filterSource) items = items.filter((r) => r.source === filterSource);
    if (filterYear) items = items.filter((r) => r.academic_year === filterYear);
    setRows(items);
  }, [branchId, filterFrom, filterTo, filterStatus, filterSource, filterYear]);
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      const [c, st, s, src] = await Promise.all([
        api.get("/classes"),
        api.get("/staff", { params: { branch_id: branchId, limit: 500 } }),
        api.get("/sessions"),
        api.get("/enquiry-sources"),
      ]);
      setClasses(c.data.items || []);
      setStaff(st.data.items || []);
      setSessions(s.data.items || []);
      setSources(src.data.items || []);
    })();
  }, [branchId]);

  const openCreate = () => {
    setForm({
      date: new Date().toISOString().slice(0, 10),
      status: "open_interested",
      sibling: "no",
      gender: "Male",
      branch_id: branchId,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.first_name || !form.last_name || !form.class_id)
      return toast.error("Required fields missing");
    await api.post("/student-enquiries", form);
    toast.success("Enquiry created");
    setOpen(false);
    load();
  };

  const classOptions = classes.map((c) => ({ value: c.id, label: c.name }));
  const staffOptions = staff.map((s) => ({
    value: s.id,
    label: `${s.first_name} ${s.last_name || ""}`,
    sub: s.role,
  }));
  const yearOptions = sessions.map((s) => ({ value: s.name, label: s.name }));
  const sourceOptions = sources.map((s) => ({ value: s.name, label: s.name }));

  return (
    <div data-testid="enquiries-page">
      <PageTitle
        title="Admission Enquiries"
        subtitle="Lead pipeline with follow-up timeline"
        actions={
          <Button
            size="sm"
            onClick={openCreate}
            className="h-8 erp-primary"
            data-testid="add-enq"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> New Enquiry
          </Button>
        }
      />

      {/* Filters */}
      <div className="border border-slate-200 bg-white rounded-md p-3 mb-3 flex flex-wrap items-end gap-2">
        <div>
          <Label className="text-[10px] font-bold uppercase mb-1 block">
            From
          </Label>
          <DatePicker
            value={filterFrom}
            onChange={setFilterFrom}
            className="h-8 w-36 text-xs"
            testId="f-from"
          />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase mb-1 block">
            To
          </Label>
          <DatePicker
            value={filterTo}
            onChange={setFilterTo}
            className="h-8 w-36 text-xs"
            testId="f-to"
          />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase mb-1 block">
            Status
          </Label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="h-8 text-xs rounded-md border border-slate-300 px-2 bg-white"
            data-testid="f-status"
          >
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase mb-1 block">
            Source
          </Label>
          <select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            className="h-8 text-xs rounded-md border border-slate-300 px-2 bg-white"
            data-testid="f-source"
          >
            <option value="">All</option>
            {sources.map((s) => (
              <option key={s.id} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase mb-1 block">
            Academic Year
          </Label>
          <select
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            className="h-8 text-xs rounded-md border border-slate-300 px-2 bg-white"
            data-testid="f-year"
          >
            <option value="">All</option>
            {sessions.map((s) => (
              <option key={s.id} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setFilterFrom("");
            setFilterTo("");
            setFilterStatus("");
            setFilterSource("");
            setFilterYear("");
          }}
          className="h-8 text-xs"
        >
          Clear
        </Button>
      </div>

      <DataTable
        testid="enq-table"
        columns={[
          { key: "sno", label: "S.No.", render: (r) => rows.indexOf(r) + 1 },
          { key: "date", label: "Date" },
          {
            key: "name",
            label: "Student",
            render: (r) => `${r.first_name || ""} ${r.last_name || ""}`,
          },
          {
            key: "class_id",
            label: "Class",
            render: (r) =>
              classes.find((c) => c.id === r.class_id)?.name || "—",
          },
          { key: "father_mobile", label: "Mobile" },
          { key: "source", label: "Source" },
          {
            key: "assigned_staff",
            label: "Staff",
            render: (r) =>
              staff.find((s) => s.id === r.assigned_staff)?.first_name || "—",
          },
          { key: "academic_year", label: "Year" },
          {
            key: "status",
            label: "Status",
            render: (r) => {
              const s = statusOf(r.status);
              return (
                <span
                  className={`px-2 py-0.5 rounded text-[10px] font-bold ${s.color}`}
                >
                  {s.label}
                </span>
              );
            },
          },
          {
            key: "actions",
            label: "Follow Up",
            render: (r) => (
              <button
                onClick={() => nav(`/front-office/enquiries/${r.id}`)}
                className="text-xs px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 font-semibold"
                data-testid={`fu-${r.id}`}
              >
                <Clock className="w-3 h-3 inline mr-1" /> Follow Up
              </button>
            ),
          },
        ]}
        rows={rows}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-display erp-text-primary">
              New Admission Enquiry
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs font-semibold">First Name *</Label>
              <Input
                value={form.first_name || ""}
                onChange={(e) =>
                  setForm({ ...form, first_name: e.target.value })
                }
                className="h-9"
                data-testid="ef-first"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Middle Name</Label>
              <Input
                value={form.middle_name || ""}
                onChange={(e) =>
                  setForm({ ...form, middle_name: e.target.value })
                }
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Last Name *</Label>
              <Input
                value={form.last_name || ""}
                onChange={(e) =>
                  setForm({ ...form, last_name: e.target.value })
                }
                className="h-9"
                data-testid="ef-last"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Date of Enquiry *</Label>
              <DatePicker
                value={form.date || ""}
                onChange={(v) => setForm({ ...form, date: v })}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Class *</Label>
              <Combobox
                value={form.class_id || ""}
                onChange={(v) => setForm({ ...form, class_id: v })}
                options={classOptions}
                placeholder="Select class"
                testid="ef-class"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Gender *</Label>
              <select
                value={form.gender || "Male"}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
                className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
              >
                <option>Male</option>
                <option>Female</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Date of Birth *</Label>
              <DatePicker
                value={form.dob || ""}
                onChange={(v) => setForm({ ...form, dob: v })}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Sibling *</Label>
              <select
                value={form.sibling || "no"}
                onChange={(e) => setForm({ ...form, sibling: e.target.value })}
                className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Academic Year *</Label>
              <Combobox
                value={form.academic_year || ""}
                onChange={(v) => setForm({ ...form, academic_year: v })}
                options={yearOptions}
                placeholder="Select year"
                testid="ef-year"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Father Name</Label>
              <Input
                value={form.father_name || ""}
                onChange={(e) =>
                  setForm({ ...form, father_name: e.target.value })
                }
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Father Mobile</Label>
              <Input
                value={form.father_mobile || ""}
                onChange={(e) =>
                  setForm({ ...form, father_mobile: e.target.value })
                }
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Mother Name</Label>
              <Input
                value={form.mother_name || ""}
                onChange={(e) =>
                  setForm({ ...form, mother_name: e.target.value })
                }
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Mother Mobile</Label>
              <Input
                value={form.mother_mobile || ""}
                onChange={(e) =>
                  setForm({ ...form, mother_mobile: e.target.value })
                }
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Email</Label>
              <Input
                value={form.email || ""}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Source</Label>
              <Combobox
                value={form.source || ""}
                onChange={(v) => setForm({ ...form, source: v })}
                options={sourceOptions}
                placeholder="Search source"
                testid="ef-source"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Assign Staff</Label>
              <Combobox
                value={form.assigned_staff || ""}
                onChange={(v) => setForm({ ...form, assigned_staff: v })}
                options={staffOptions}
                placeholder="Search staff"
                testid="ef-staff"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold">Status *</Label>
              <select
                value={form.status || "open_interested"}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
                data-testid="ef-status"
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold">Notes</Label>
              <textarea
                rows={2}
                value={form.notes || ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full rounded-md border border-slate-300 text-xs px-2 py-1.5"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              className="erp-primary"
              data-testid="ef-save"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* Follow-up detail page with timeline + summary panel */
export function EnquiryDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { branchId } = useAuth();
  const [enq, setEnq] = useState(null);
  const [classes, setClasses] = useState([]);
  const [staff, setStaff] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [form, setForm] = useState({
    follow_up_date: new Date().toISOString().slice(0, 10),
  });
  const [status, setStatus] = useState("");

  const load = async () => {
    const [e, c, s, fu] = await Promise.all([
      api.get(`/student-enquiries/${id}`),
      api.get("/classes"),
      api.get("/staff", { params: { branch_id: branchId, limit: 500 } }),
      api.get("/enquiry-responses", { params: { limit: 500 } }),
    ]);
    setEnq(e.data);
    setClasses(c.data.items || []);
    setStaff(s.data.items || []);
    setFollowUps(
      (fu.data.items || [])
        .filter((x) => x.enquiry_id === id)
        .sort((a, b) =>
          (b.follow_up_date || "").localeCompare(a.follow_up_date || ""),
        ),
    );
    setStatus(e.data?.status || "");
  };
  useEffect(() => {
    load();
  }, [id]);

  const addFollowUp = async () => {
    if (!form.response) return toast.error("Response required");
    await api.post("/enquiry-responses", {
      ...form,
      enquiry_id: id,
      created_at: new Date().toISOString(),
    });
    if (form.next_follow_up_date) {
      await api.put(`/student-enquiries/${id}`, {
        ...enq,
        next_follow_up_date: form.next_follow_up_date,
        last_follow_up_date: form.follow_up_date,
      });
    }
    toast.success("Follow-up saved");
    setForm({ follow_up_date: new Date().toISOString().slice(0, 10) });
    load();
  };

  const updateStatus = async () => {
    await api.put(`/student-enquiries/${id}`, { ...enq, status });
    toast.success("Status updated");
    load();
  };

  if (!enq)
    return <div className="p-6 text-center text-slate-400">Loading…</div>;
  const sObj = statusOf(enq.status);
  const cls = classes.find((c) => c.id === enq.class_id);
  const stf = staff.find((s) => s.id === enq.assigned_staff);

  return (
    <div data-testid="enquiry-detail">
      <div className="flex items-center justify-between mb-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => nav("/front-office/enquiries")}
          className="h-8"
        >
          <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
        </Button>
        <h1 className="text-lg md:text-xl font-bold tracking-tight font-serif-display erp-text-primary">
          Follow Up · {enq.first_name} {enq.last_name}
        </h1>
        <div />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Left: follow-up form + timeline */}
        <div className="lg:col-span-2 space-y-3">
          <div className="border border-slate-200 bg-white rounded-md p-4 shadow-sm">
            <h3 className="text-sm font-bold mb-3 erp-text-primary font-serif-display">
              Add Follow Up
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">
                  Follow Up Date *
                </Label>
                <DatePicker
                  value={form.follow_up_date}
                  onChange={(v) => setForm({ ...form, follow_up_date: v })}
                  className="h-9"
                  testId="fu-date"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">
                  Next Follow Up *
                </Label>
                <DatePicker
                  value={form.next_follow_up_date || ""}
                  onChange={(v) => setForm({ ...form, next_follow_up_date: v })}
                  className="h-9"
                  testId="fu-next"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-semibold">Response *</Label>
                <textarea
                  rows={2}
                  value={form.response || ""}
                  onChange={(e) =>
                    setForm({ ...form, response: e.target.value })
                  }
                  className="w-full rounded-md border border-slate-300 text-sm px-2 py-1.5"
                  data-testid="fu-response"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-semibold">Note</Label>
                <textarea
                  rows={2}
                  value={form.note || ""}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="w-full rounded-md border border-slate-300 text-sm px-2 py-1.5"
                />
              </div>
            </div>
            <Button
              onClick={addFollowUp}
              className="mt-3 erp-primary"
              data-testid="fu-save"
            >
              Save Follow Up
            </Button>
          </div>

          <div className="border border-slate-200 bg-white rounded-md p-4 shadow-sm">
            <h3 className="text-sm font-bold mb-3 erp-text-primary font-serif-display">
              Follow Up Timeline
            </h3>
            {followUps.length === 0 && (
              <div className="text-xs text-slate-400 py-6 text-center">
                No follow-ups yet
              </div>
            )}
            <div className="relative pl-6 space-y-3">
              {followUps.map((f, i) => (
                <div key={f.id} className="relative">
                  <div className="absolute -left-6 top-1 w-4 h-4 rounded-full erp-primary flex items-center justify-center">
                    <Clock className="w-2.5 h-2.5 text-white" />
                  </div>
                  {i < followUps.length - 1 && (
                    <div className="absolute -left-4 top-5 bottom-0 w-px bg-slate-200" />
                  )}
                  <div className="border border-slate-200 rounded-md p-3 hover:shadow-sm transition">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs font-bold erp-text-primary">
                        {f.follow_up_date}
                      </div>
                      {f.next_follow_up_date && (
                        <div className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                          Next: {f.next_follow_up_date}
                        </div>
                      )}
                    </div>
                    <div className="text-sm text-slate-800">{f.response}</div>
                    {f.note && (
                      <div className="text-[11px] text-slate-500 mt-1 italic">
                        {f.note}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: summary */}
        <div className="border border-slate-200 bg-slate-50 rounded-md p-4 shadow-sm h-fit space-y-2 text-sm">
          <div className="flex items-center justify-between pb-2 border-b border-slate-200">
            <h3 className="font-bold erp-text-primary font-serif-display">
              Summary
            </h3>
            <div>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="h-8 text-xs rounded-md border border-slate-300 px-2 bg-white"
                data-testid="status-select"
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                onClick={updateStatus}
                className="h-8 erp-primary ml-1 text-xs"
                data-testid="status-change"
              >
                Change
              </Button>
            </div>
          </div>
          <div
            className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${sObj.color}`}
          >
            {sObj.label}
          </div>
          <Row icon={Cal} label="Enquiry Date" value={enq.date} />
          <Row
            icon={Cal}
            label="Last Follow Up"
            value={enq.last_follow_up_date || "—"}
          />
          <Row
            icon={Cal}
            label="Next Follow Up"
            value={enq.next_follow_up_date || "—"}
          />
          <Row icon={MessageCircle} label="Source" value={enq.source || "—"} />
          <Row icon={User} label="Class" value={cls?.name || "—"} />
          <Row icon={User} label="Father" value={enq.father_name || "—"} />
          <Row
            icon={Phone}
            label="Father Mobile"
            value={enq.father_mobile || "—"}
          />
          <Row icon={User} label="Mother" value={enq.mother_name || "—"} />
          <Row
            icon={Phone}
            label="Mother Mobile"
            value={enq.mother_mobile || "—"}
          />
          <Row icon={Mail} label="Email" value={enq.email || "—"} />
          <Row icon={User} label="Staff" value={stf?.first_name || "—"} />
          <Row
            icon={Cal}
            label="Academic Year"
            value={enq.academic_year || "—"}
          />
        </div>
      </div>
    </div>
  );
}
function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-xs py-1">
      <Icon className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] text-slate-500 uppercase">{label}</div>
        <div className="font-semibold text-slate-800 truncate">{value}</div>
      </div>
    </div>
  );
}
